import {
  CreateParams,
  CreateResult,
  DataProvider,
  GetListResult,
  GetOneParams,
  GetOneResult,
  Identifier,
  UpdateParams,
  UpdateResult,
  GetManyParams,
  GetManyResult,
  DeleteParams,
} from 'react-admin'
import { PRINT_ITEM_PARAMETERS_URL } from '../../api-urls'
import {
  get,
  getWithPagination,
  patch,
  post,
  remove,
} from '../../common/fetch.utils'
import {
  filterParamsComposer,
  queryParamsComposer,
} from '../../common/get-by-conditions.utils'
import { TicketAPIGetListParams } from '../../common/ticket-api-get-list.params'
import filterMapper from './print-item-parameters-filter.mapper'
import { mapSortPrintItemParametersParam } from './print-item-parameters-sort.mapper'
import { printItemParametersDto } from '../../dto/print-item-parameters.dto'
import { PrintParameterTypes } from '../../enum/PrintParameterTypes'

const provider = {
  getList: async (
    resource: string,
    { filter, sort, pagination }: TicketAPIGetListParams,
  ): Promise<GetListResult<printItemParametersDto>> => {
    const filterParams = `o=>${filterParamsComposer('o', filter, filterMapper)}`
    const pathParams = queryParamsComposer(
      sort,
      pagination,
      mapSortPrintItemParametersParam,
    )
    const path = `/${filterParams}${(pathParams && `?${pathParams}`) || ''}`

    const {
      data,
      range: { total },
    } = await getWithPagination<printItemParametersDto[]>(
      `${PRINT_ITEM_PARAMETERS_URL}/GetByConditions`,
      path,
    )

    return Promise.resolve({
      data,
      total,
    })
  },
  getOne: async (
    resource: string,
    { id }: GetOneParams,
  ): Promise<GetOneResult<printItemParametersDto>> => {
    const data = await get<printItemParametersDto>(
      PRINT_ITEM_PARAMETERS_URL,
      `/${id}`,
    )
    return Promise.resolve({
      data,
    })
  },
  create: async (
    resource,
    { data }: CreateParams<CreatePrintItemParametersRequest>,
  ): Promise<CreateResult<printItemParametersDto>> => {
    const printItemParameters = await post<
      CreatePrintItemParametersRequest[],
      printItemParametersDto[]
    >(PRINT_ITEM_PARAMETERS_URL, [data])
    return {
      data: printItemParameters[0],
    }
  },
  update: async (
    resource: string,
    { id, data }: UpdateParams,
  ): Promise<UpdateResult> => {
    const update = await patch<
      { id: Identifier; [n: string]: any },
      printItemParametersDto
    >(PRINT_ITEM_PARAMETERS_URL, { id, ...data })
    return Promise.resolve({ data: update })
  },
  delete: async (
    resource: string,
    { id }: DeleteParams,
  ): Promise<CreateResult> => {
    const deleted = await remove<printItemParametersDto>(
      PRINT_ITEM_PARAMETERS_URL,
      `/Delete/${id}`,
    )

    return Promise.resolve({ data: deleted })
  },
  getMany: async (
    resource: string,
    { ids }: GetManyParams,
  ): Promise<GetManyResult<printItemParametersDto>> => {
    const data = await get<printItemParametersDto[]>(
      PRINT_ITEM_PARAMETERS_URL,
      `/GetByConditions/o=>new int[] {${ids.toString()}}.Contains(o.Id)`,
    )
    return Promise.resolve({ data })
  },
} as DataProvider

export interface CreatePrintItemParametersRequest {
  readonly key: string
  readonly options: any
  readonly type: PrintParameterTypes
  readonly printItemId: number
}

export default provider
